import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";


const FALLBACK = { matches_total: 4200, matches_last_30_days: 312 };

const useCountUp = (target: number, active: boolean, duration = 1200) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);

  return value;
};



export const MatchesCounterSection = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);

  const { data } = useQuery({
    queryKey: ["match-stats"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_match_stats");
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return FALLBACK;
      return {
        matches_total: Number(row.matches_total) || 0,
        matches_last_30_days: Number(row.matches_last_30_days) || 0,
      };
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const stats = data ?? FALLBACK;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const total = useCountUp(stats.matches_total, inView);
  const lastThirtyDays = stats.matches_last_30_days;

  return (
    <section ref={sectionRef} className="bg-white py-12 md:py-16">
      <div className="max-w-6xl w-full mx-auto px-4">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="font-heading font-black text-3xl md:text-5xl mb-3">
            Matches die{" "}
            <span className="relative inline-block">
              <span className="relative z-10">écht</span>
              <span className="absolute left-0 -bottom-1 w-full h-2.5 md:h-3 bg-yellow -z-0 rounded-sm" />
            </span>{" "}
            gemaakt worden
          </h2>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
            Iedere dag vinden mensen en bedrijven elkaar op ChillWerk.nl

          </p>
        </div>

        <div
          className={`max-w-3xl mx-auto transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {/* Deze maand - hoofdkaart */}
          <div className="relative rounded-[28px] bg-gradient-to-br from-cyan-light/60 via-white to-yellow/10 border border-cyan/25 shadow-[0_20px_60px_-25px_hsl(var(--primary)/0.45)] px-6 py-10 md:px-10 md:py-12 text-center overflow-hidden">
            <div className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-cyan/20 blur-3xl" />
            <div className="absolute top-4 right-4 md:top-5 md:right-5 inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-3 py-1.5 border border-emerald-500/20">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span className="text-[11px] font-bold uppercase tracking-wider text-foreground/70">Live</span>
            </div>
            <div className="relative">
              <p className="font-heading font-black leading-none flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1">
                <span className="text-6xl sm:text-7xl md:text-8xl tabular-nums">
                  {total.toLocaleString("nl-NL")}
                </span>
                <span className="text-2xl sm:text-3xl md:text-4xl text-foreground/50">matches</span>
              </p>
              <p className="text-base md:text-lg text-foreground/70 mt-4">
                Maak jij de volgende?
              </p>
            </div>
          </div>

          <p className="mt-6 text-center text-base text-foreground/60">
            🔥{" "}
            <span className="font-semibold tabular-nums text-foreground">
              +{lastThirtyDays.toLocaleString("nl-NL")}
            </span>{" "}
            matches afgelopen maand
          </p>


        </div>

      </div>
    </section>
  );
};
